/**
 * fetch + ReadableStream-based SSE client for `/api/meetings/:id/events`.
 *
 * Why not native `EventSource`?
 *   - SPEC §8 / NFR-6 / FR-42 require resuming with `Last-Event-ID` after a
 *     network drop. Native `EventSource` does send this header on its OWN
 *     reconnects, but offers no API to read or set it from JS — so a manual
 *     close → re-open round-trip (which is exactly what our exponential
 *     backoff needs) cannot replay missed events.
 *   - We need explicit control over reconnect timing (1s → 2s → 4s … capped at
 *     30s). Native EventSource picks an unspecified delay.
 *   - UI_PLAN §6 explicitly recommends the fetch + ReadableStream path.
 *
 * The protocol parser handles a minimal subset of the SSE spec sufficient for
 * the contract in SPEC §8 (`event:`, `data:`, `id:`, blank-line dispatch,
 * `:hb` comments). Multi-line `data:` are joined with `\n`.
 */

import type { UUID } from "@/api/types";

export type PhaseStatus = "start" | "progress" | "done" | "error";

export interface PhaseEvent {
  meeting_id: UUID;
  phase: 1 | 2 | 3 | 4 | 5;
  status: PhaseStatus;
  attempt: number;
  message?: string;
  error?: {
    code: string;
    message: string;
    provider: string | null;
  };
}

export interface SseClientOptions {
  /** Base URL prefix; default '' (same-origin / vite proxy). */
  baseUrl?: string;
  /** Initial backoff in ms (default 1000). */
  initialBackoffMs?: number;
  /** Backoff cap in ms (default 30_000). */
  maxBackoffMs?: number;
  /** Inject a custom fetch (used by tests). */
  fetchImpl?: typeof fetch;
  /** Notifies the caller when parsing fails (non-JSON / bad payload). */
  onParseError?: (raw: string, cause: unknown) => void;
  /** Notifies the caller on each (re)connect attempt. */
  onReconnect?: (delayMs: number, attempt: number) => void;
}

/**
 * Subscribes to the SSE phase-stream for one meeting. `start()` must be
 * called at most once; subsequent calls are no-ops. `close()` is idempotent
 * and prevents any further reconnect.
 */
export class MeetingEventStream {
  private readonly options: Required<
    Pick<SseClientOptions, "baseUrl" | "initialBackoffMs" | "maxBackoffMs">
  > &
    Pick<SseClientOptions, "fetchImpl" | "onParseError" | "onReconnect">;
  private controller: AbortController | null = null;
  private lastEventId: string | null = null;
  private closed = false;
  private started = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;

  constructor(options: SseClientOptions = {}) {
    this.options = {
      baseUrl: options.baseUrl ?? "",
      initialBackoffMs: options.initialBackoffMs ?? 1000,
      maxBackoffMs: options.maxBackoffMs ?? 30_000,
      fetchImpl: options.fetchImpl,
      onParseError: options.onParseError,
      onReconnect: options.onReconnect,
    };
  }

  /**
   * Open the stream and dispatch parsed `PhaseEvent`s to `onEvent`. Reconnects
   * automatically with exponential backoff until `close()` is called.
   */
  start(meetingId: UUID, onEvent: (event: PhaseEvent) => void): void {
    if (this.started || this.closed) return;
    this.started = true;
    void this.connect(meetingId, onEvent);
  }

  /** Idempotent. Prevents any further reconnect. */
  close(): void {
    if (this.closed) return;
    this.closed = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.controller !== null) {
      this.controller.abort();
      this.controller = null;
    }
  }

  private async connect(
    meetingId: UUID,
    onEvent: (event: PhaseEvent) => void,
  ): Promise<void> {
    if (this.closed) return;
    const ctrl = new AbortController();
    this.controller = ctrl;
    const url = `${this.options.baseUrl}/api/meetings/${meetingId}/events`;
    const headers: Record<string, string> = { Accept: "text/event-stream" };
    if (this.lastEventId !== null) {
      headers["Last-Event-ID"] = this.lastEventId;
    }
    const fetcher = this.options.fetchImpl ?? globalThis.fetch;
    try {
      const response = await fetcher(url, {
        method: "GET",
        credentials: "include",
        headers,
        signal: ctrl.signal,
      });
      if (!response.ok || response.body === null) {
        throw new Error(`SSE connect failed: ${response.status}`);
      }
      // Note: we do NOT reset reconnectAttempt on connection-open. A flaky
      // proxy that drops the stream immediately after handshake would loop
      // at the initial backoff forever otherwise. Counter resets after the
      // stream has delivered at least one event (see `dispatchBlock`).
      await this.consume(response.body, onEvent);
    } catch (err) {
      if (this.closed) return;
      // Any failure (network, abort-via-error, bad status) → backoff retry.
      void err;
    }
    if (!this.closed) {
      this.scheduleReconnect(meetingId, onEvent);
    }
  }

  private async consume(
    body: ReadableStream<Uint8Array>,
    onEvent: (event: PhaseEvent) => void,
  ): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nlIdx = buffer.indexOf("\n\n");
      while (nlIdx !== -1) {
        const block = buffer.slice(0, nlIdx);
        buffer = buffer.slice(nlIdx + 2);
        this.dispatchBlock(block, onEvent);
        nlIdx = buffer.indexOf("\n\n");
      }
    }
  }

  private dispatchBlock(
    block: string,
    onEvent: (event: PhaseEvent) => void,
  ): void {
    if (block.length === 0) return;
    const dataLines: string[] = [];
    let eventId: string | null = null;
    for (const rawLine of block.split("\n")) {
      const line = rawLine.replace(/\r$/, "");
      if (line.length === 0 || line.startsWith(":")) continue;
      const sepIdx = line.indexOf(":");
      const field = sepIdx === -1 ? line : line.slice(0, sepIdx);
      const value =
        sepIdx === -1 ? "" : line.slice(sepIdx + 1).replace(/^ /, "");
      if (field === "data") dataLines.push(value);
      else if (field === "id") eventId = value;
      // We accept `event:` field but the PhaseEvent payload carries
      // `phase`/`status` already, so we don't need to dispatch by name.
    }
    if (eventId !== null) this.lastEventId = eventId;
    if (dataLines.length === 0) return;
    const raw = dataLines.join("\n");
    try {
      const parsed = JSON.parse(raw) as PhaseEvent;
      // Successful delivery → stream is healthy; reset backoff window.
      this.reconnectAttempt = 0;
      onEvent(parsed);
    } catch (err) {
      this.options.onParseError?.(raw, err);
    }
  }

  private scheduleReconnect(
    meetingId: UUID,
    onEvent: (event: PhaseEvent) => void,
  ): void {
    const delay = Math.min(
      this.options.initialBackoffMs * 2 ** this.reconnectAttempt,
      this.options.maxBackoffMs,
    );
    this.reconnectAttempt += 1;
    this.options.onReconnect?.(delay, this.reconnectAttempt);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.closed) return;
      void this.connect(meetingId, onEvent);
    }, delay);
  }
}
